import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Header, Image, Message, Icon, useFrontier, Table, Modal, Checkbox } from 'react-frontier2';
import { CDN_URL } from '../../AdminConfig';
import { addCommas, bindClick, bindFormChange, fromUnix, fromUnixLocal } from '@arema/components/Util';
import { SetLoading } from '@arema/components/Classes';
import { useUser } from '../../AdminHooks';
import API from '../../API';
import UserAccess from '../../UserAccess';

interface RefundableData{
	requests: {
		user_id: number,
		refundable_web: boolean,
		refundable_pdv: boolean,
		refundable_complete: boolean,
		comments: string,
		date_created: number,
		user_first_name: string,
		user_last_name: string,
		user_name: string,
	}[],
	sales: {
		tickets_pdv: number,
		tickets_web: number,
	},
	date: {
		date_id: number,
		date: number,
		date_start: number,
		refundable_web: boolean,
		refundable_pdv: boolean,
		refundable_complete: boolean,
	},
	event: {
		event_id: number,
		event_name: string,
		promoter_name: string,
	}
}

var RefundableRequest = ()=>{
	var params = useParams();
	var { Toast } = useFrontier();
	var { hasAccess } = useUser();
	var [data, setData] = useState<RefundableData>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [completed, setCompleted] = useState<boolean>(false);
	var [selectedRequest, setSelectedRequest] = useState<RefundableData["requests"][0]>(null);

	useEffect(()=>{
		if(!data && params.id && !Number.isNaN(parseInt(params.id)) && hasAccess(UserAccess.DATES.SET_REFUNDABLE)){
			API.getDateRefundableRequests(parseInt(params.id), true, true, true).then(res=>{
				if(res.error) return setLoadError(res.message);
				setData(res.data);
			}).catch(err=>{
				setLoadError('Hubo un error inesperado cargando las solicitudes de reembolso. (LCL-1)');
			});
		}
	}, []);
	
	if(!hasAccess(UserAccess.DATES.SET_REFUNDABLE)){
		return <Header text='Sin acceso' subtext='No tienes acceso a esta herramienta' iconName='ban' />
	}

	if(!data || loadError){
		return <Header text={loadError ? 'Error' : 'Cargando solicitudes...'} subtext={loadError} loading={!loadError} iconName={loadError ? 'face-frown-open' : null} />
	}

	var acceptRequest = (user_id: number)=>{
		return ()=>{
			var request = data.requests.find(a=>a.user_id==user_id);
			if(!request) return;
			setSelectedRequest({ ...request });
		}
	}

	var sendResponse = (setLoading: SetLoading)=>{
		if(!selectedRequest) return;
		if(selectedRequest.refundable_complete && !hasAccess(UserAccess.DATES.SET_REFUNDABLE_COMPLETE)) return;
		setLoading(true);
		API.editDateRefundOptions(data.date.date_id, selectedRequest.refundable_web, selectedRequest.refundable_pdv, selectedRequest.refundable_complete, true).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			var d = { ...data };
			d.date.refundable_web = selectedRequest.refundable_web;
			d.date.refundable_pdv = selectedRequest.refundable_pdv;
			d.date.refundable_complete = selectedRequest.refundable_complete;
			setCompleted(true);
			setData(d);
			setSelectedRequest(null);
			Toast.success('Se ha realizado el cambio de configuración.');
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado enviando la solicitud de reembolsable (LCL-2)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var hasAccessComplete = hasAccess(UserAccess.DATES.SET_REFUNDABLE_COMPLETE);
	var onRequestFormChange = bindFormChange(selectedRequest, setSelectedRequest);

	return <div>
		<div className="ar centered event">
			<Image src={`${CDN_URL}/events/${data.event.event_id}/400.webp`} alt={data.event.event_name} />
			<div className="info">
				<div className="name">{data.event.event_name}</div>
				<div className="venue">{data.event.promoter_name}</div>
			</div>
		</div>
		<Table style={{ maxWidth: 400, margin: 'auto' }}>
			<Table.Head
				title={`Calendario ${data.date.date_id}`}
				titleRight={(
					<Button size='tiny' text='Ver calendario' as={Link} to={`/events/${data.event.event_id}/dates/${data.date.date_id}`} />
				)}
			/>
			<Table.Body>
				<Table.Row data={['Fecha', fromUnix(data.date.date).toFormat('dd/MMM/yy HH:mm')]} />
				{!!data.date.date_start && (
					<Table.Row data={['Inicio venta', fromUnix(data.date.date_start).toFormat('dd/MMM/yy HH:mm')]} />
				)}
				<Table.Row data={['Ventas', addCommas(data.sales.tickets_web+data.sales.tickets_pdv, false)]} />
				<Table.Row data={['Ventas web', addCommas(data.sales.tickets_web, false)]} />
				<Table.Row data={['Ventas PDV', addCommas(data.sales.tickets_pdv, false)]} />
				<Table.Cell row header colSpan={2} value={'Configuración actual'} />
				<Table.Cell data={['Reembolsable web', <Icon name={data.date.refundable_web ? 'check-circle' : 'times-circle'} color={data.date.refundable_web ? 'green' : 'grey'} />]} />
				<Table.Cell data={['Reembolsable PDV', <Icon name={data.date.refundable_pdv ? 'check-circle' : 'times-circle'} color={data.date.refundable_pdv ? 'green' : 'grey'} />]} />
				<Table.Cell data={['Reembolso completo', <Icon name={data.date.refundable_complete ? 'check-circle' : 'times-circle'} color={data.date.refundable_complete ? 'green' : 'grey'} />]} />
			</Table.Body>
		</Table>
		{completed || data.requests.length==0 ? (
			<Header text='Sin solicitudes' iconName='face-grin-wide' subtext='No hay solicitudes pendientes' style={{ marginTop: 30 }} />
		) : <>
			{data.requests.length>1 ? (
				<Header text='Solicitudes' style={{ marginTop: 30 }} />
			) : (
				<div style={{ height: 15 }}></div>
			)}
			{data.requests.map((a, i)=>(
				<Table details key={`req-${a.user_id}-${data.date.date_id}`} style={{ margin: 'auto', maxWidth: 500, marginBottom: 15 }}>
					<Table.Head
						title={`Solicitud #${i+1}`}
					/>
					<Table.Body>
						<Table.Row data={['Creador', `${a.user_first_name} ${a.user_last_name}`]} />
						<Table.Row data={['Creación', fromUnixLocal(a.date_created).toFormat('DD/MMM/YY HH:mm:ss')]} />
						<Table.Row data={['Reembolsable web', <Icon name={a.refundable_web ? 'check-circle' : 'times-circle'} color={a.refundable_web ? 'green' : 'grey'} />]} />
						<Table.Row data={['Reembolsable PDV', <Icon name={a.refundable_pdv ? 'check-circle' : 'times-circle'} color={a.refundable_pdv ? 'green' : 'grey'} />]} />
						<Table.Row data={['Reembolso completo', <Icon name={a.refundable_complete ? 'check-circle' : 'times-circle'} color={a.refundable_complete ? 'green' : 'grey'} />]} />
						<Table.Row data={['Comentarios', a.comments]} />
					</Table.Body>
					<Table.Footer actions={(
						<Button text='Aceptar' color='black' onClick={acceptRequest(a.user_id)} />
					)} />
				</Table>
			))}
		</>}
		<Modal open={!!selectedRequest} onClose={bindClick(setSelectedRequest)} size='tiny'>
			<Modal.Header>Configuración de reembolso</Modal.Header>
			<Modal.Content>
				{!selectedRequest?.refundable_complete || hasAccessComplete ? (
					<Message text='Puedes cambiar la configuración de la solicitud si es necesario.' style={{ marginBottom: 20 }} />
				) : null}
				<div className="fr option">
					<div className="info">
						<div className="title">Reembolsable Web</div>
						<div className="description">Permitir solicitudes de reembolso de compras web para este calendario.</div>
					</div>
					<Checkbox checked={!!selectedRequest?.refundable_web} onChange={onRequestFormChange('refundable_web')} />
				</div>
				<div className="fr option">
					<div className="info">
						<div className="title">Reembolsable PDV</div>
						<div className="description">Permitir solicitudes de reembolso de compras pdv para este calendario.</div>
					</div>
					<Checkbox checked={!!selectedRequest?.refundable_pdv} onChange={onRequestFormChange('refundable_pdv')} />
				</div>
				<div className="fr option last" style={{ marginBottom: 15 }}>
					<div className="info">
						<div className="title">Reembolsable Completo</div>
						<div className="description">
							Reembolsar toda la compra <b>INCLUYENDO cargos por servicio, entrega y forma de pago</b>. 
							{!hasAccessComplete && (
								<span style={{ marginLeft: 5, color: 'brown' }}>No tienes acceso a realizar este cambio</span>
							)}
						</div>
					</div>
					{hasAccessComplete || selectedRequest?.refundable_complete ? (
						<Checkbox disabled={!hasAccessComplete} checked={!!selectedRequest?.refundable_complete} onChange={onRequestFormChange('refundable_complete')} />
					) : (
						<Icon name={'ban'} />
					)}
				</div>
				{selectedRequest?.refundable_complete && !hasAccessComplete ? (
					<Message text='Ya que la solicitud pide reembolso completo, esta solicitud la tendrá que aprobar alguien con acceso a marcar calendarios como reembolso completo.' type='error' />
				) : null}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setSelectedRequest)} />
				{!selectedRequest?.refundable_complete || hasAccessComplete ? (
					<Button text='Aceptar' color='black' onClick={sendResponse} />
				) : null}
			</Modal.Actions>
		</Modal>
	</div>
}

export default RefundableRequest;